/**
 * 加载遮罩组件 — 覆盖在父容器上的半透明加载指示器。
 *
 * 主要导出：`CrmLoadingOverlay`（自定义元素 `<crm-loading-overlay>`）
 *
 * 功能特性：
 * - 通过 `active` 属性控制显示/隐藏（使用 reflect 映射到属性，支持 CSS 属性选择器）
 * - 激活时显示居中旋转动画和半透明白色遮罩
 * - 未激活时透明且不接收指针事件
 * - 使用 `position: absolute` 定位，覆盖父容器（父容器需 position: relative）
 * - 使用 `backdrop-filter: blur(2px)` 实现背景模糊效果
 *
 * 注意：此组件直接继承 `LitElement`（非 BaseElement），样式自包含。
 *
 * 用法：
 *   <div style="position:relative">
 *     <crm-loading-overlay ?active=${this.loading}></crm-loading-overlay>
 *     <!-- 内容 -->
 *   </div>
 */
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('crm-loading-overlay')
export class CrmLoadingOverlay extends LitElement {
  /** 是否激活（显示遮罩），使用 reflect 映射到 DOM 属性以支持 CSS 选择器 */
  @property({ type: Boolean, reflect: true })
  active = false;

  static styles = css`
    /* 默认状态：透明且不接收指针事件 */
    :host {
      display: block;
      position: absolute;
      inset: 0;
      z-index: 10;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(2px);
      transition: opacity 0.2s ease;
      opacity: 0;
      pointer-events: none;
    }
    /* 激活状态：不透明且接收指针事件（阻止用户交互） */
    :host([active]) {
      opacity: 1;
      pointer-events: auto;
    }
    .spinner-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
    /* 旋转动画指示器 */
    .spinner {
      width: 28px;
      height: 28px;
      border: 3px solid var(--tw-color-primary, #000000);
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  /**
   * 渲染加载遮罩。
   * 仅渲染旋转动画容器，显示/隐藏由 :host([active]) CSS 选择器控制。
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="spinner-container">
        <div class="spinner"></div>
      </div>
    `;
  }
}